import { useId, useState, type FormEvent } from 'react';
import { Button } from '@tervior-tech/tervior-shared-ui-react';
import { Dialog } from '../../components/ui/Dialog';
import { Label } from '../../components/ui/Label';
import { Input } from '../../components/ui/Input';
import { FieldError } from '../../components/ui/FieldError';
import { CopyField } from '../../components/ui/CopyField';
import { Alert } from '../../components/ui/Alert';
import { ApiProblemError, createApiKey, type ApiKeyMode, type CreatedApiKey } from '../../lib/api-client';

interface CreateKeyDialogProps {
  open: boolean;
  /** Firebase ID token — `/internal/api-keys` auth, never the dashboard session key. `null` while auth is still resolving. */
  idToken: string | null;
  onClose: () => void;
  onCreated: (key: CreatedApiKey) => void;
}

/** Create-key modal — name + mode form, then the raw value shown exactly once with a copy button (same "segredo mostrado uma vez" convention as onboarding's `ApiKeyStep.tsx`). */
export function CreateKeyDialog({ open, idToken, onClose, onCreated }: CreateKeyDialogProps) {
  const nameId = useId();
  const modeId = useId();
  const [name, setName] = useState('');
  const [mode, setMode] = useState<ApiKeyMode>('live');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const [created, setCreated] = useState<CreatedApiKey | null>(null);

  function handleClose() {
    setName('');
    setMode('live');
    setError(undefined);
    setCreated(null);
    onClose();
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!idToken) {
      return;
    }
    if (!name.trim()) {
      setError('Dê um nome para identificar essa chave.');
      return;
    }
    setSubmitting(true);
    setError(undefined);
    try {
      const result = await createApiKey(idToken, name.trim(), mode);
      setCreated(result);
      onCreated(result);
    } catch (err) {
      setError(err instanceof ApiProblemError ? (err.problem.detail ?? err.problem.title) : 'Não foi possível criar a chave agora.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onClose={handleClose} title={created ? 'Chave criada' : 'Criar chave de API'}>
      {created ? (
        <div className="flex flex-col gap-4">
          <Alert variant="warning" title="Isso só aparece uma vez">
            Copie e guarde essa chave agora. Depois de fechar esta janela, o Trivio nunca mais mostra o valor
            completo — só o prefixo, para identificação.
          </Alert>
          <CopyField label={`${created.mode === 'live' ? 'trv_live_' : 'trv_test_'}…`} value={created.key} />
          <div className="flex justify-end">
            <Button type="button" variant="default" onClick={handleClose}>
              Fechar
            </Button>
          </div>
        </div>
      ) : (
        <form className="flex flex-col gap-4" onSubmit={handleSubmit} noValidate>
          <div className="flex flex-col gap-2">
            <Label htmlFor={nameId}>Nome</Label>
            <Input
              id={nameId}
              value={name}
              onChange={(event) => setName(event.target.value)}
              invalid={Boolean(error)}
              aria-describedby={error ? `${nameId}-error` : undefined}
              disabled={submitting}
              placeholder="Ex.: integração ERP"
            />
            <FieldError id={`${nameId}-error`} message={error} />
          </div>
          <div className="flex flex-col gap-2">
            <Label htmlFor={modeId}>Modo</Label>
            <select
              id={modeId}
              value={mode}
              onChange={(event) => setMode(event.target.value as ApiKeyMode)}
              disabled={submitting}
              className="rounded-none border border-input bg-background px-3 py-2 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
            >
              <option value="live">Produção (live)</option>
              <option value="test">Sandbox (test)</option>
            </select>
          </div>
          <div className="flex justify-end gap-3">
            <Button type="button" variant="outline" onClick={handleClose} disabled={submitting}>
              Cancelar
            </Button>
            <Button type="submit" variant="default" disabled={submitting}>
              {submitting ? 'Criando…' : 'Criar chave'}
            </Button>
          </div>
        </form>
      )}
    </Dialog>
  );
}
